import type {
  ApiError,
  BootstrapResponse,
  LeadCreateRequest,
  Language,
  MissionResponse,
  SalesPlaybook,
  SalesOutcomeType,
} from "../shared/types";

async function request<T>(input: RequestInfo | URL, init?: RequestInit): Promise<T> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 20_000);
  try {
    const response = await fetch(input, {
      ...init,
      signal: controller.signal,
      headers: {
        "Content-Type": "application/json",
        ...init?.headers,
      },
    });
    const body = (await response.json()) as T | ApiError;
    if (!response.ok) {
      const error = body as ApiError;
      throw new Error(error.error?.message ?? "Omar could not complete the request.");
    }
    return body as T;
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw new Error(
        "Omar took too long to respond. Please retry; the deterministic workflow remains available.",
      );
    }
    throw error;
  } finally {
    window.clearTimeout(timeout);
  }
}

export function getBootstrap(accountId = "brightcup-cn-us") {
  return request<BootstrapResponse>(`/api/bootstrap?accountId=${encodeURIComponent(accountId)}`);
}

export function createMission(
  accountId: string,
  objective: string,
  language: Language,
  playbook: SalesPlaybook,
) {
  return request<MissionResponse>("/api/missions", {
    method: "POST",
    body: JSON.stringify({
      accountId,
      objective,
      language,
      playbook,
    }),
  });
}

export function createLead(input: LeadCreateRequest) {
  return request<BootstrapResponse>("/api/leads", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function importLeads(leads: LeadCreateRequest[]) {
  return request<{
    created: number;
    duplicates: number;
    failed: number;
    results: Array<{ accountId?: string; created: boolean; name: string; error?: string }>;
  }>("/api/leads/import", {
    method: "POST",
    body: JSON.stringify({ leads }),
  });
}

export function getMission(missionId: string) {
  return request<MissionResponse>(`/api/missions/${missionId}`);
}

export function approveTask(missionId: string, taskId: string) {
  return request<MissionResponse>(
    `/api/missions/${missionId}/tasks/${taskId}/approve`,
    {
      method: "POST",
      body: JSON.stringify({ confirm: true, actor: "Portfolio demo operator" }),
    },
  );
}

export function executeTask(missionId: string, taskId: string) {
  return request<MissionResponse>(
    `/api/missions/${missionId}/tasks/${taskId}/execute`,
    {
      method: "POST",
      body: JSON.stringify({ confirm: true, actor: "Portfolio demo operator" }),
    },
  );
}

export function rollbackTask(missionId: string, taskId: string) {
  return request<MissionResponse>(
    `/api/missions/${missionId}/tasks/${taskId}/rollback`,
    {
      method: "POST",
      body: JSON.stringify({ confirm: true, actor: "Portfolio demo operator" }),
    },
  );
}

export function updateTaskProposal(
  missionId: string,
  taskId: string,
  proposal: {
    channel: "email" | "wechat" | "call";
    subject: string;
    message: string;
    messageZh: string;
    followUpDays: number;
    meetingCta: string;
  },
) {
  return request<MissionResponse>(
    `/api/missions/${missionId}/tasks/${taskId}/proposal`,
    {
      method: "PATCH",
      body: JSON.stringify(proposal),
    },
  );
}

export function recordSalesOutcome(
  accountId: string,
  missionId: string,
  outcome: SalesOutcomeType,
  note: string,
) {
  return request<MissionResponse>(`/api/leads/${accountId}/outcomes`, {
    method: "POST",
    body: JSON.stringify({
      missionId,
      outcome,
      note,
      actor: "Portfolio demo operator",
    }),
  });
}

export function syncCrmLead(accountId: string) {
  return request<BootstrapResponse>(`/api/leads/${accountId}/crm-sync`, {
    method: "POST",
  });
}

export function sendCustomerReply(
  accountId: string,
  missionId: string,
  input: {
    channel: "email" | "wechat" | "call";
    language: Language;
    message: string;
  },
) {
  return request<MissionResponse>(`/api/leads/${accountId}/replies`, {
    method: "POST",
    body: JSON.stringify({ missionId, ...input }),
  });
}
